import React, { createContext, useContext, useState } from "react";
import { ErrorCategory } from "../Utils/errorClassifier";

export type ProcessingStep =
  | "jd-input"
  | "jd-review"
  | "resume-confirm"
  | "job-post-processing"
  | "profile-matching"
  | "completed"
  | "error";

export type FailedStep = 'job-post-generation' | 'resume-upload' | 'profile-matching';

export type CaseType = "form-based";

interface UserOnboardingContextType {
  screeningSessionId: string | null;
  setScreeningSessionId: (id: string | null) => void;

  caseType: CaseType;
  setCaseType: (type: CaseType) => void;

  /** Raw cta_type from the sign-up URL: 'validate' | 'save' | 'free' | null */
  ctaType: string | null;
  setCtaType: (type: string | null) => void;

  jobDescription: string;
  setJobDescription: (desc: string) => void;

  resumeFile: File | null;
  setResumeFile: (file: File | null) => void;

  currentStep: ProcessingStep;
  setCurrentStep: (step: ProcessingStep) => void;

  jobPostId: string | null;
  setJobPostId: (id: string | null) => void;

  jobApplicationId: string | null;
  setJobApplicationId: (id: string | null) => void;

  errorMessage: string | null;
  setErrorMessage: (msg: string | null) => void;

  errorCategory: ErrorCategory | null;
  setErrorCategory: (cat: ErrorCategory | null) => void;

  failedStep: FailedStep | null;
  setFailedStep: (step: FailedStep | null) => void;

  reset: () => void;
}

const UserOnboardingContext = createContext<UserOnboardingContextType | null>(null);

export const UserOnboardingProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [screeningSessionId, setScreeningSessionId] = useState<string | null>(null);
  const [caseType, setCaseType] = useState<CaseType>("form-based");
  const [ctaType, setCtaType] = useState<string | null>(null);
  const [jobDescription, setJobDescription] = useState("");
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [currentStep, setCurrentStep] = useState<ProcessingStep>("jd-input");
  const [jobPostId, setJobPostId] = useState<string | null>(null);
  const [jobApplicationId, setJobApplicationId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errorCategory, setErrorCategory] = useState<ErrorCategory | null>(null);
  const [failedStep, setFailedStep] = useState<FailedStep | null>(null);

  const reset = () => {
    setCurrentStep("jd-input");
    setJobPostId(null);
    setJobApplicationId(null);
    setErrorMessage(null);
    setResumeFile(null);
    setErrorCategory(null);
    setFailedStep(null);
  };

  return (
    <UserOnboardingContext.Provider
      value={{
        screeningSessionId,
        setScreeningSessionId,
        caseType,
        setCaseType,
        ctaType,
        setCtaType,
        jobDescription,
        setJobDescription,
        resumeFile,
        setResumeFile,
        currentStep,
        setCurrentStep,
        jobPostId,
        setJobPostId,
        jobApplicationId,
        setJobApplicationId,
        errorMessage,
        setErrorMessage,
        errorCategory,
        setErrorCategory,
        failedStep,
        setFailedStep,
        reset,
      }}
    >
      {children}
    </UserOnboardingContext.Provider>
  );
};

export const useUserOnboarding = (): UserOnboardingContextType => {
  const ctx = useContext(UserOnboardingContext);
  if (!ctx) {
    throw new Error("useUserOnboarding must be used within UserOnboardingProvider");
  }
  return ctx;
};

export default UserOnboardingContext;
